import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { getRetention, getStickiness } from "../../http/analytics";
import { cachedFetch } from "../../http/analyticsCache";
import RatioChart from "./RatioChart";

/**
 * Reports page: stickiness (Engagement tab) and weekly cohort retention
 * (Retention tab). Data comes from useRetentionData so the page owns the
 * filters, status and refresh.
 */

import { NAVY } from "./ReportParts";
// a16z consumer-social bars for DAU/MAU
const DAU_MAU_BENCHMARKS = [
  { value: 0.25, label: "OK 25%" },
  { value: 0.4, label: "Good 40%" },
  { value: 0.5, label: "Great 50%" },
];
const dauMauVerdict = (v) =>
  v >= 0.5
    ? { label: "Great (a16z 50%+)", tone: "good" }
    : v >= 0.4
    ? { label: "Good (a16z 40%+)", tone: "good" }
    : v >= 0.25
    ? { label: "OK (a16z 25%+)", tone: "ok" }
    : { label: "Below the 25% OK bar", tone: "low" };
const COHORT_WEEKS = 8;

const pct = (v, digits = 1) =>
  v == null ? "—" : `${(v * 100).toFixed(digits)}%`;

const headerCellStyle = {
  fontWeight: 700,
  color: "#fff",
  backgroundColor: NAVY,
  fontSize: { xs: "12px", sm: "13px", md: "14px" },
  whiteSpace: "nowrap",
};

const Card = ({ label, value, hint }) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid #e0e0e0",
      borderRadius: "8px",
      padding: "16px 20px",
      minWidth: { xs: "100%", sm: "180px" },
      flex: { xs: "1 1 100%", sm: "0 1 auto" },
    }}
  >
    <Typography
      sx={{ fontSize: "13px", color: "text.secondary", fontWeight: 600 }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: { xs: "24px", md: "28px" },
        fontWeight: 700,
        color: NAVY,
      }}
    >
      {value}
    </Typography>
    {hint && (
      <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
        {hint}
      </Typography>
    )}
  </Paper>
);

const Notice = ({ children }) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px dashed #c9c9c9",
      borderRadius: "8px",
      padding: "20px",
      color: "text.secondary",
      fontSize: "13px",
      marginBottom: "16px",
    }}
  >
    {children}
  </Paper>
);

/** Cell tint scales with the rate so strong cohorts stand out at a glance. */
const RateCell = ({ cell }) => {
  if (!cell || cell.eligible === 0) {
    return (
      <TableCell
        sx={{ color: "text.secondary", fontSize: "13px" }}
        title="Not enough days have passed yet"
      >
        —
      </TableCell>
    );
  }
  const alpha = Math.min(0.85, 0.08 + (cell.rate || 0) * 1.6);
  return (
    <TableCell
      title={`${cell.returned.toLocaleString()} of ${cell.eligible.toLocaleString()} came back`}
      sx={{
        fontSize: "13px",
        fontWeight: 600,
        color: alpha > 0.5 ? "#fff" : NAVY,
        backgroundColor: `rgba(20, 44, 92, ${alpha})`,
      }}
    >
      {pct(cell.rate)}
    </TableCell>
  );
};

export const POPULATIONS = [
  { id: "activated", label: "Activated (5+ votes)" },
  { id: "all", label: "All accounts" },
];

export function useRetentionData({ timeZone, dateRange, population }) {
  const [stick, setStick] = useState({
    loading: true,
    unavailable: false,
    data: null,
  });
  const [ret, setRet] = useState({
    loading: true,
    unavailable: false,
    data: null,
  });

  const load = useCallback(
    (force = false) => {
      const base = { timezone: timeZone, population };
      const rangeParams = { ...base };
      if (dateRange?.[0] && dateRange?.[1]) {
        rangeParams.startDate = dayjs(dateRange[0]).format("YYYY-MM-DD");
        rangeParams.endDate = dayjs(dateRange[1]).format("YYYY-MM-DD");
      }
      setStick((p) => ({ ...p, loading: true }));
      setRet((p) => ({ ...p, loading: true }));
      cachedFetch("stickiness", getStickiness, rangeParams, { force })
        .then((r) =>
          setStick({
            loading: false,
            unavailable: false,
            data: r?.data || null,
          })
        )
        .catch((e) => {
          console.error("Failed to load stickiness:", e);
          setStick({ loading: false, unavailable: true, data: null });
        });
      cachedFetch(
        "retention",
        getRetention,
        { ...base, weeks: COHORT_WEEKS },
        { force }
      )
        .then((r) =>
          setRet({ loading: false, unavailable: false, data: r?.data || null })
        )
        .catch((e) => {
          console.error("Failed to load retention:", e);
          setRet({ loading: false, unavailable: true, data: null });
        });
    },
    [timeZone, dateRange, population]
  );

  useEffect(() => {
    load();
  }, [load]);

  return { stick, ret, load };
}

export function StickinessPanel({ stick, timeZone }) {
  // Today is still in progress, so every figure stops at the last full day.
  const todayKey = new Intl.DateTimeFormat("en-CA", {
    timeZone: timeZone === "UTC" ? "UTC" : "America/New_York",
  }).format(new Date());
  const days = (stick.data?.days || []).filter((d) => d.date < todayKey);
  const latest = days[days.length - 1];
  const creators = stick.data?.creators;

  if (stick.unavailable)
    return <Notice>Not available on this backend yet.</Notice>;
  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <Card label="MAU" value={latest ? latest.mau.toLocaleString() : "—"} />
        <Card label="WAU" value={latest ? latest.wau.toLocaleString() : "—"} />
        <Card label="DAU" value={latest ? latest.dau.toLocaleString() : "—"} />
        <Card label="Creator rate" value={pct(creators?.allTimeRate)} />
      </Box>
      {days.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            gap: 2,
          }}
        >
          <RatioChart
            title="Daily habit"
            metric="DAU / MAU"
            points={days.map((d) => ({ ...d, value: d.dauMau }))}
            benchmarks={DAU_MAU_BENCHMARKS}
            verdict={dauMauVerdict}
            tooltip={(p) =>
              `DAU ${p.dau.toLocaleString()} of MAU ${p.mau.toLocaleString()}`
            }
          />
          <RatioChart
            title="Weekly habit"
            metric="WAU / MAU"
            points={days.map((d) => ({ ...d, value: d.wauMau }))}
            benchmarks={[]}
            tooltip={(p) =>
              `WAU ${p.wau.toLocaleString()} of MAU ${p.mau.toLocaleString()}`
            }
          />
        </Box>
      )}
    </>
  );
}

export function CohortPanel({ ret, timeZone }) {
  const offsets = ret.data?.offsets || [1, 7, 14, 28];
  if (ret.unavailable)
    return <Notice>Not available on this backend yet.</Notice>;
  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ border: "1px solid #e0e0e0", borderRadius: "8px" }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellStyle}>Cohort ({timeZone})</TableCell>
            <TableCell sx={headerCellStyle}>Users</TableCell>
            {offsets.map((n) => (
              <TableCell key={n} sx={headerCellStyle}>
                Day {n}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {(ret.data?.cohorts || []).map((c) => (
            <TableRow key={c.weekStart} hover>
              <TableCell sx={{ fontSize: "13px", whiteSpace: "nowrap" }}>
                {dayjs(c.weekStart).format("MMM D")} –{" "}
                {dayjs(c.weekEnd).format("MMM D")}
              </TableCell>
              <TableCell
                sx={{ fontSize: "13px", fontWeight: 600, color: NAVY }}
              >
                {c.size.toLocaleString()}
              </TableCell>
              {offsets.map((n) => (
                <RateCell key={n} cell={c.retention[`d${n}`]} />
              ))}
            </TableRow>
          ))}
          {ret.data?.overall && (
            <TableRow>
              <TableCell
                sx={{ fontSize: "13px", fontWeight: 700, color: NAVY }}
              >
                All cohorts
              </TableCell>
              <TableCell />
              {offsets.map((n) => (
                <RateCell key={n} cell={ret.data.overall[`d${n}`]} />
              ))}
            </TableRow>
          )}
          {!ret.data?.cohorts?.length && (
            <TableRow>
              <TableCell
                colSpan={offsets.length + 2}
                align="center"
                sx={{ padding: "32px" }}
              >
                {ret.loading ? "" : "No cohorts yet"}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
